import type { QueryClient } from '@tanstack/vue-query'

/**
 * Cache Hydration Utilities
 * Handles TanStack Query cache operations for middleware
 */

export interface CacheHydrationOptions {
  queryClient: QueryClient
  queryKey: readonly unknown[]
  fetchFn: () => Promise<unknown>
}

/**
 * Hydrates TanStack Query cache with data if not already present
 * @param options - Cache hydration configuration
 * @returns Promise<unknown> - The cached or fetched data
 */
export async function hydrateCache(options: CacheHydrationOptions): Promise<unknown> {
  const { queryClient, queryKey, fetchFn } = options

  // Check if data already exists in cache
  const existingData = queryClient.getQueryData(queryKey)

  if (existingData) {
    return existingData
  }

  // Fetch data and hydrate cache

  const data = await fetchFn()

  // Hydrate the cache
  queryClient.setQueryData(queryKey, data)

  return data
}

/**
 * Always fetches fresh data and writes it to the cache (use when a cached answer may be stale,
 * e.g. access that can be revoked server-side).
 */
export async function refreshCache(options: CacheHydrationOptions): Promise<unknown> {
  const { queryClient, queryKey, fetchFn } = options
  const data = await fetchFn()
  queryClient.setQueryData(queryKey, data)
  return data
}

/**
 * Gets data from TanStack Query cache
 * @param queryClient - TanStack Query client instance
 * @param queryKey - Query key to look up
 * @returns unknown - Cached data or null
 */
export function getCachedData(queryClient: QueryClient, queryKey: readonly unknown[]): unknown {
  return queryClient.getQueryData(queryKey)
}

/**
 * Sets data in TanStack Query cache
 * @param queryClient - TanStack Query client instance
 * @param queryKey - Query key to set
 * @param data - Data to cache
 */
export function setCachedData(queryClient: QueryClient, queryKey: readonly unknown[], data: unknown): void {
  queryClient.setQueryData(queryKey, data)
  console.log('💾 [Cache] Data set for key:', queryKey)
}
